import { useEffect, useState } from 'react'

import { Box, Typography, makeStyles } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import ComponentLoader from '@scoir/components-core/dist/components/ComponentLoader'
import cookie from 'cookie'
import { FormikHelpers } from 'formik'
import { Link, useLocation } from 'react-router-dom'

import OrLine from 'components/OrLine'
import PasswordForm, { PasswordFormValues } from 'components/RegistrationForm/passwordForm'
import SSOButtons from 'components/SSOButtons'
import Title from 'components/Title'

import ContactCounselorForm from './contactCounselorForm'
import { useRegisterParentUser, useRegisterStudentUser, validateParentInvite, validateStudentInvite } from './helper'
import { getSSOStatus } from './helper/ssoHelper.ts'
import { UserType } from './helper/types.ts'
import InviteErrorConstants from './inviteErrorConstants.ts'
import ValidationError from './validationError'

const useStyles = makeStyles((theme) => ({
  link: {
    color: theme.palette.primary.dark,
    textDecoration: 'underline',
    margin: theme.spacing(2, 0),
    display: 'inline-block',
    cursor: 'pointer',
  },
}))

type TokenAuth = {
  Email: string
  ConfirmationCode: string
  Token: string
  MatchedHighSchoolStudent?: {
    FirstName: string
    LastName: string
    HighSchoolStudentId: string
  }
  HighSchool?: {
    HighSchoolId: string
  }
  FirstName: string
  IsMiddleSchoolStudent?: boolean
  minPasswordLength?: number
}

type RegisterFromInviteProps = {
  token?: string
  userType: 'parent' | 'student'
  id?: string
  code?: string
}
// The Google callback page sends a student back here with the reason its
// confirmation was refused
const useErrorCode = () => {
  const { search } = useLocation()
  return new URLSearchParams(search).get('errorCode') ?? undefined
}

const RegisterFromInviteRoute = ({ token, id, code, userType }: RegisterFromInviteProps) => {
  const classes = useStyles()
  const errorCode = useErrorCode()
  const [tokenAuth, setTokenAuth] = useState<TokenAuth | undefined>(undefined)
  const [user, setUser] = useState<UserType>({ name: '', location: '' })
  const [error, setError] = useState<boolean>(false)
  const [loading, setLoading] = useState(true)
  const [initialValues, setInitialValues] = useState<PasswordFormValues | undefined>(undefined)
  const [confirmError, setConfirmError] = useState(false)
  const [showContactCounselor, setShowContactCounselor] = useState(false)
  const [ssoError, setSSOError] = useState<string | undefined>(undefined)

  useEffect(() => {
    setSSOError(InviteErrorConstants(errorCode) ?? undefined)
  }, [errorCode])

  useEffect(() => {
    // Validate Parent with Id and Code from URL
    if (userType === 'parent' && id && code) {
      validateParentInvite(id, code, setError, setInitialValues, setLoading, setTokenAuth, setUser)
      return
    }
    // Validate Student with token from URL
    if (userType === 'student' && token) {
      validateStudentInvite(token, setError, setInitialValues, setLoading, setTokenAuth, setUser)
      return
    }
    // Nothing to validate, and both helpers clear this themselves otherwise
    setLoading(false)
  }, [code, id, token, userType])

  const registerParentUser = useRegisterParentUser()
  const registerStudentUser = useRegisterStudentUser()

  const clearCookies = () => {
    const path = `/student/confirm/link/${tokenAuth?.HighSchool?.HighSchoolId}/${token}`
    document.cookie = cookie.serialize('remembered', path, {
      path: '/',
    })
  }

  const registerUser = (values: PasswordFormValues, props: FormikHelpers<PasswordFormValues>) => {
    props.setSubmitting(true)
    // Register Parent
    if (userType === 'parent') {
      registerParentUser(values, tokenAuth, setConfirmError, props.setSubmitting)
    }
    // Register Student
    if (userType === 'student') {
      registerStudentUser(values, tokenAuth, setConfirmError, props.setSubmitting)
    }
    props.setSubmitting(false)
  }

  const getSubtitle = () => {
    if (userType === 'student' && user.highschool && user.location) {
      return `Please activate your account with ${user.highschool} in ${user.location}`
    }
    if (userType === 'parent' && user.email) {
      return `Please activate your account for ${user.email}.`
    }
    return 'Please activate your account.'
  }
  const handleNotYou = () => {
    setShowContactCounselor(!showContactCounselor)
  }

  if (error) {
    return <ValidationError />
  }
  if (showContactCounselor) {
    return (
      <ContactCounselorForm
        onCancel={handleNotYou}
        student={tokenAuth?.MatchedHighSchoolStudent}
        highschool={tokenAuth?.HighSchool}
        email={tokenAuth?.Email}
      />
    )
  }

  const {
    cleverEnabled,
    classLinkEnabled,
    ssoEnabled,
    cleverRequired,
    classLinkRequired,
    googleRequired,
    ssoRequired,
  } = getSSOStatus(user, userType)

  return (
    <ComponentLoader loaded={!loading}>
      {ssoError && (
        <Box mb={2}>
          <Alert severity="error">{ssoError}</Alert>
        </Box>
      )}
      <Title
        title={`Welcome${user.name ? `, ${user.name}` : ''} ✨`}
        subtitle={getSubtitle()}
      />
      {tokenAuth?.HighSchool?.HighSchoolId && !tokenAuth.IsMiddleSchoolStudent && !googleRequired && (
        <Link
          to={`/student/confirm/link/${tokenAuth.HighSchool.HighSchoolId}/${token}`}
          onClick={clearCookies}
          className={classes.link}
        >
          Sign in with an existing Scoir account
        </Link>
      )}
      {!ssoRequired && (
        <PasswordForm
          initialValue={initialValues}
          handleSubmit={registerUser}
          confirmError={confirmError}
          useCaptcha={userType === 'student'}
          minPasswordLength={tokenAuth?.minPasswordLength}
        />
      )}
      {userType === 'student' && (
        <Typography color="textSecondary">
          Not you?{' '}
          <span
            className={classes.link}
            onClick={handleNotYou}
          >
            Let us know
          </span>
        </Typography>
      )}
      {ssoEnabled && !ssoRequired && <OrLine />}
      {(ssoEnabled || ssoRequired) && (
        <SSOButtons
          disableClassLink={!classLinkEnabled && !classLinkRequired}
          disableClever={!cleverEnabled && !cleverRequired}
          disableGoogle={!googleRequired}
          inviteToken={token}
          onError={setSSOError}
        />
      )}
    </ComponentLoader>
  )
}

export default RegisterFromInviteRoute
